import { BTSessionStatus, SupervisionNoteStatus } from '../../constants';
import { getSupervisionStatus } from '../../utils/common';
import { ServiceCodes } from './billings/claims/constants';
import ProgressIcon from '../../assets/images/progress-new.svg';
import AwaitingReviewIcon from '../../assets/images/awaiting-review-new.svg';
import AwaitingFinalisationIcon from '../../assets/images/awaiting.svg';
import EditIcon from '../../assets/images/edit-new.svg';
import CheckIcon from '../../assets/images/check-new.svg';
import AwaitingGuardianSignIcon from '../../assets/images/awaiting-guardian-sign.svg';

import type { AssessmentNoteStatus } from '../../constants';

interface IStatusIconRenderer {
  procedureCode?: ServiceCodes;
  status?: SupervisionNoteStatus | BTSessionStatus | AssessmentNoteStatus;
  isAwaitingFinalization?: boolean;
  iconWidth?: number;
  iconHeight?: number;
  disabled?: boolean;
  sx?: any;
}

const getSessionStatus = (statusId: BTSessionStatus, isAwaitingFinalization?: boolean) => {
  if (isAwaitingFinalization) {
    return { icon: AwaitingFinalisationIcon, testId: 'awaitingFinalizationIcon' };
  }

  switch (statusId) {
    case BTSessionStatus.Started:
      return { icon: ProgressIcon, testId: 'progressIcon' };
    case BTSessionStatus.AwaitingReview:
      return { icon: AwaitingReviewIcon, testId: 'awaitingReviewIcon' };
    case BTSessionStatus.EditsRequested:
      return { icon: EditIcon, testId: 'editRequestIcon' };
    case BTSessionStatus.Completed:
      return { icon: CheckIcon, testId: 'completedIcon' };
    case BTSessionStatus.AwaitingGuardianSignature:
      return { icon: AwaitingGuardianSignIcon, testId: 'awaitingGaurdianSignIcon' };
    default:
      return { icon: '', testId: '' };
  }
};

function getStatusIcon(
  procedureCode: ServiceCodes,
  status: SupervisionNoteStatus | BTSessionStatus | AssessmentNoteStatus,
  isAwaitingFinalization?: boolean,
) {
  if (procedureCode === ServiceCodes.SessionProcedureCode) {
    const sessionStatus = Object.values(BTSessionStatus).find(
      (sessionStatus) => sessionStatus === status,
    );
    return sessionStatus
      ? getSessionStatus(sessionStatus, isAwaitingFinalization)
      : undefined;
  }
  if (
    procedureCode === ServiceCodes.AssessmentCode ||
    procedureCode === ServiceCodes.AssistingAssessmentCode ||
    procedureCode === ServiceCodes.ClientSNProcedureCode ||
    procedureCode === ServiceCodes.ParentSNProcedureCode ||
    procedureCode === ServiceCodes.GroupSNProcedureCode
  ) {
    const supervisionStatus = Object.values(SupervisionNoteStatus).find(
      (supervisionStatus) => supervisionStatus === status,
    );
    const iconDetails = getSupervisionStatus(supervisionStatus);
    return iconDetails ? { icon: iconDetails.icon, testId: `sn${iconDetails.status}` } : undefined;
  }
}

function StatusIconRenderer({
  procedureCode,
  status,
  iconHeight = 32,
  iconWidth = 32,
  disabled = false,
  isAwaitingFinalization,
  sx,
}: IStatusIconRenderer) {
  if (!procedureCode || !status) return <></>;
  const iconDetails = getStatusIcon(
    procedureCode,
    status,
    isAwaitingFinalization,
  );
  if (!iconDetails) return <></>;
  const { icon, testId } = iconDetails;
  return (
    <img
      data-testid={testId}
      src={icon}
      alt="icon"
      height={iconHeight}
      width={iconWidth}
      style={{
        opacity: disabled ? 0.4 : 1,
        ...sx,
      }}
    />
  );
}

export default StatusIconRenderer;
